import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useCart } from '@/contexts/CartContext';
import { readPreferredShippingMethodId, savePreferredShippingMethodId } from '@/services/checkoutPreferences';
import { ShippingDeliveryType, ShippingMethodQuote, fetchShippingMethods } from '@/services/shipping';
import { withImageFallback } from '@/utils/images';

const deliveryTypeLabels: Record<ShippingDeliveryType, string> = {
  HomeDelivery: '宅配',
  StorePickup: '門市自取',
  ConvenienceStorePickup: '超商取貨',
};

export default function CartPage() {
  const navigate = useNavigate();
  const { items, updateQuantity, removeItem, clearCart, totalQuantity, subtotal } = useCart();
  const [shippingMethods, setShippingMethods] = useState<ShippingMethodQuote[]>([]);
  const [shippingMethodId, setShippingMethodId] = useState(() => readPreferredShippingMethodId());
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [shippingError, setShippingError] = useState('');

  useEffect(() => {
    if (items.length === 0) {
      setShippingMethods([]);
      setShippingMethodId('');
      savePreferredShippingMethodId('');
      return;
    }

    let alive = true;
    setLoadingShipping(true);
    setShippingError('');
    fetchShippingMethods(subtotal)
      .then((methods) => {
        if (!alive) return;
        setShippingMethods(methods);
        setShippingMethodId((current) => {
          if (current && methods.some((method) => method.shippingMethodId === current)) {
            savePreferredShippingMethodId(current);
            return current;
          }
          const fallback = methods[0]?.shippingMethodId || '';
          savePreferredShippingMethodId(fallback);
          return fallback;
        });
      })
      .catch(() => {
        if (alive) setShippingError('配送方式載入失敗，請重新整理頁面後再試。');
      })
      .finally(() => {
        if (alive) setLoadingShipping(false);
      });

    return () => {
      alive = false;
    };
  }, [items.length, subtotal]);

  const selectedShippingMethod = useMemo(
    () => shippingMethods.find((method) => method.shippingMethodId === shippingMethodId) || null,
    [shippingMethodId, shippingMethods]
  );
  const regularShippingMethods = useMemo(
    () => shippingMethods.filter((method) => method.deliveryType !== 'ConvenienceStorePickup'),
    [shippingMethods]
  );
  const cvsShippingMethods = useMemo(
    () => shippingMethods.filter((method) => method.deliveryType === 'ConvenienceStorePickup'),
    [shippingMethods]
  );
  const cvsBrandCounts = useMemo(
    () => cvsShippingMethods.reduce<Record<string, number>>((counts, method) => ({ ...counts, [method.cvsStoreBrand]: (counts[method.cvsStoreBrand] || 0) + 1 }), {}),
    [cvsShippingMethods]
  );
  const cvsGroupSelected = selectedShippingMethod?.deliveryType === 'ConvenienceStorePickup';
  const estimatedShippingFee = selectedShippingMethod?.fee ?? 0;
  const estimatedTotal = subtotal + estimatedShippingFee;

  function chooseShippingMethod(methodId: string) {
    setShippingMethodId(methodId);
    savePreferredShippingMethodId(methodId);
  }

  function chooseCvsGroup() {
    const nextMethodId = cvsGroupSelected ? shippingMethodId : cvsShippingMethods[0]?.shippingMethodId || '';
    if (nextMethodId) chooseShippingMethod(nextMethodId);
  }

  function goCheckout() {
    if (shippingMethodId) savePreferredShippingMethodId(shippingMethodId);
    navigate('/checkout');
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-14 md:pt-20">
        <div className="border-b border-[#E8E0D6] bg-[#FAF8F5] px-6 py-8 md:px-10 md:py-12 lg:px-16">
          <div className="mx-auto max-w-6xl">
            <nav className="mb-2 flex items-center gap-2 text-sm text-[#9A8A7A]">
              <button type="button" onClick={() => navigate('/')} className="hover:text-[#1B3D2A]">首頁</button>
              <i className="ri-arrow-right-s-line" />
              <span className="font-medium text-[#1B3D2A]">購物車</span>
            </nav>
            <h1 className="text-2xl font-bold text-[#1B3D2A] md:text-3xl">我的購物車</h1>
            <p className="mt-1 text-sm text-[#5A6B5A]">{totalQuantity > 0 ? `共 ${totalQuantity} 件商品` : '購物車目前沒有商品'}</p>
          </div>
        </div>

        <div className="mx-auto max-w-6xl px-6 py-8 md:px-10 md:py-12 lg:px-16">
          {items.length === 0 ? (
            <div className="flex flex-col items-center rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] py-20 text-center">
              <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-white">
                <i className="ri-shopping-cart-line text-2xl text-[#9A8A7A]" />
              </div>
              <p className="text-lg font-bold text-[#1B3D2A]">購物車目前沒有商品</p>
              <p className="mt-2 text-sm text-[#9A8A7A]">可以先到商品頁挑選適合的助聽方案。</p>
              <button
                type="button"
                onClick={() => navigate('/products')}
                className="mt-6 rounded-xl bg-[#1B3D2A] px-6 py-3 text-sm font-bold text-white hover:bg-[#143026]"
              >
                前往商品列表
              </button>
            </div>
          ) : (
            <div className="grid gap-8 lg:grid-cols-3 lg:items-start">
              <div className="lg:col-span-2">
                <div className="overflow-hidden rounded-2xl border border-[#E8E0D6]">
                  {items.map((item, index) => (
                    <div
                      key={item.key}
                      className={`flex flex-col gap-4 p-5 sm:flex-row sm:items-center md:p-6 ${index !== items.length - 1 ? 'border-b border-[#E8E0D6]' : ''}`}
                    >
                      <div className="h-24 w-24 shrink-0 overflow-hidden rounded-xl bg-[#FAF8F5]">
                        <img src={withImageFallback(item.image)} alt={item.name} className="h-full w-full object-cover object-top" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <button type="button" className="text-left" onClick={() => navigate(`/product/${item.slug}`)}>
                          <p className="font-bold text-[#1B3D2A] hover:underline">{item.name}</p>
                        </button>
                        {item.variantLabel && <p className="mt-1 text-sm text-[#9A8A7A]">規格：{item.variantLabel}</p>}
                        <p className="mt-1 text-xs text-[#9A8A7A]">SKU：{item.sku || '-'}</p>
                        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                          <div className="flex items-center gap-1 rounded-full border border-[#E8E0D6]">
                            <button
                              type="button"
                              className="flex h-9 w-9 items-center justify-center text-[#5A6B5A] hover:text-[#1B3D2A]"
                              onClick={() => updateQuantity(item.key, item.quantity - 1)}
                              aria-label="減少數量"
                            >
                              <i className="ri-subtract-line" />
                            </button>
                            <span className="w-8 text-center text-sm font-bold text-[#1B3D2A]">{item.quantity}</span>
                            <button
                              type="button"
                              className="flex h-9 w-9 items-center justify-center text-[#5A6B5A] hover:text-[#1B3D2A] disabled:opacity-40"
                              onClick={() => updateQuantity(item.key, item.quantity + 1)}
                              disabled={item.maxStock !== null && item.quantity >= item.maxStock}
                              aria-label="增加數量"
                            >
                              <i className="ri-add-line" />
                            </button>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-base font-bold text-[#1B3D2A]">NT$ {(item.unitPrice * item.quantity).toLocaleString()}</span>
                            <button
                              type="button"
                              onClick={() => removeItem(item.key)}
                              className="text-sm text-[#9A8A7A] hover:text-[#c3423f]"
                            >
                              移除
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => clearCart()}
                  className="mt-4 text-sm font-medium text-[#9A8A7A] hover:text-[#c3423f]"
                >
                  清空購物車
                </button>
              </div>

              <div className="sticky top-20 z-10 rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] p-6 md:top-24">
                <h2 className="text-base font-bold text-[#1B3D2A]">訂單摘要</h2>
                <div className="mt-4 flex items-center justify-between text-sm text-[#5A6B5A]">
                  <span>商品小計</span>
                  <span>NT$ {subtotal.toLocaleString()}</span>
                </div>

                <div className="mt-5 border-t border-[#E8E0D6] pt-4">
                  <h3 className="text-sm font-bold text-[#1B3D2A]">配送方式</h3>
                  {loadingShipping ? (
                    <p className="mt-2 text-sm text-[#9A8A7A]">配送方式載入中...</p>
                  ) : shippingError ? (
                    <p className="mt-2 text-sm text-[#c3423f]">{shippingError}</p>
                  ) : shippingMethods.length === 0 ? (
                    <p className="mt-2 text-sm text-[#c3423f]">目前沒有可用的配送方式。</p>
                  ) : (
                    <div className="mt-3 grid gap-2">
                      {regularShippingMethods.map((method) => {
                        const checked = method.shippingMethodId === shippingMethodId;
                        return (
                          <label
                            key={method.shippingMethodId}
                            className={`cursor-pointer rounded-xl border bg-white p-3 transition ${
                              checked ? 'border-[#1B3D2A] ring-2 ring-[#1B3D2A]/10' : 'border-[#E8E0D6] hover:border-[#1B3D2A]/40'
                            }`}
                          >
                            <input
                              checked={checked}
                              className="sr-only"
                              name="cart-shipping-method"
                              type="radio"
                              onChange={() => chooseShippingMethod(method.shippingMethodId)}
                            />
                            <span className="flex items-start justify-between gap-2">
                              <span>
                                <span className="block text-sm font-bold text-[#1B3D2A]">{method.name}</span>
                                <span className="mt-0.5 block text-xs text-[#9A8A7A]">{deliveryTypeLabels[method.deliveryType]}</span>
                                {method.requiresStoreMap && <span className="mt-1 block text-xs text-[#9A6B2F]">需於結帳選擇超商門市</span>}
                              </span>
                              <span className="shrink-0 text-sm font-bold text-[#1B3D2A]">{method.fee > 0 ? `NT$ ${method.fee.toLocaleString()}` : '免運'}</span>
                            </span>
                            {method.description && <span className="mt-1 block text-xs text-[#9A8A7A]">{method.description}</span>}
                          </label>
                        );
                      })}
                      {cvsShippingMethods.length > 0 && (
                        <div
                          className={`rounded-xl border bg-white p-3 transition ${
                            cvsGroupSelected ? 'border-[#1B3D2A] ring-2 ring-[#1B3D2A]/10' : 'border-[#E8E0D6] hover:border-[#1B3D2A]/40'
                          }`}
                        >
                          <label className="block cursor-pointer">
                            <input
                              checked={cvsGroupSelected}
                              className="sr-only"
                              name="cart-shipping-method"
                              type="radio"
                              onChange={chooseCvsGroup}
                            />
                            <span className="flex items-start justify-between gap-2">
                              <span>
                                <span className="block text-sm font-bold text-[#1B3D2A]">超商取貨</span>
                                <span className="mt-0.5 block text-xs text-[#9A8A7A]">可選 7-ELEVEN / 全家 / 萊爾富 / OK 超商</span>
                              </span>
                              <span className="shrink-0 text-sm font-bold text-[#1B3D2A]">{estimatedShippingFee > 0 && cvsGroupSelected ? `NT$ ${estimatedShippingFee.toLocaleString()}` : cvsGroupSelected ? '免運' : '選擇後計算'}</span>
                            </span>
                          </label>
                          {cvsGroupSelected && (
                            <div className="mt-3 grid gap-2 sm:grid-cols-2">
                              {cvsShippingMethods.map((method) => {
                                const checked = method.shippingMethodId === shippingMethodId;
                                return (
                                  <button
                                    key={method.shippingMethodId}
                                    type="button"
                                    onClick={() => chooseShippingMethod(method.shippingMethodId)}
                                    className={`rounded-lg border px-3 py-2 text-left text-sm transition ${
                                      checked ? 'border-[#1B3D2A] bg-[#1B3D2A]/5 text-[#1B3D2A]' : 'border-[#E8E0D6] text-[#5A6B5A] hover:border-[#1B3D2A]/40'
                                    }`}
                                  >
                                    <span className="block font-bold">{cvsBrandCounts[method.cvsStoreBrand] > 1 ? method.name : method.cvsStoreBrandLabel}</span>
                                    <span className="mt-0.5 block text-xs text-[#9A8A7A]">{method.logisticsSubTypeLabel || method.cvsStoreBrandLabel}</span>
                                    <span className="mt-0.5 block text-xs text-[#9A8A7A]">{method.fee > 0 ? `NT$ ${method.fee.toLocaleString()}` : '免運'}{method.requiresStoreMap ? ' · 電子地圖選店' : ' · 手動門市資料'}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-between text-sm text-[#5A6B5A]">
                  <span>預估運費</span>
                  <span>{estimatedShippingFee > 0 ? `NT$ ${estimatedShippingFee.toLocaleString()}` : '免運'}</span>
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-[#E8E0D6] pt-4">
                  <span className="font-bold text-[#1B3D2A]">預估總計</span>
                  <span className="text-xl font-bold text-[#1B3D2A]">NT$ {estimatedTotal.toLocaleString()}</span>
                </div>
                <button
                  type="button"
                  onClick={goCheckout}
                  disabled={loadingShipping || shippingMethods.length === 0 || !shippingMethodId}
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#1B3D2A] py-3.5 text-sm font-bold text-white hover:bg-[#143026] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className="ri-secure-payment-line" />
                  前往結帳
                </button>
                <p className="mt-3 text-center text-xs leading-5 text-[#9A8A7A]">
                  結帳頁仍會重新確認配送、付款與優惠資料，實際總金額以送出訂單前為準。
                </p>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
